import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

/**
 * PasswordResetRequestPage Component
 * 
 * Password recovery request page where users enter their email
 * to receive a reset link. The reset link expires after 60 minutes.
 * 
 * Features:
 * - Email input with validation
 * - Success confirmation message
 * - Error handling for invalid/unknown emails
 * - Link back to login page
 * 
 * @validates Requirements 5.8, 7.5
 */
export function PasswordResetRequestPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Validates email format
   * Must contain exactly one "@" followed by a domain with at least one "."
   */
  const isValidEmail = (email: string): boolean => {
    const parts = email.split('@');
    if (parts.length !== 2) return false;
    const domain = parts[1];
    return domain !== undefined && domain.includes('.') && domain.length > 2;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email format
    if (!isValidEmail(email)) {
      setError(t('passwordReset.request.errors.invalidEmail'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/password/reset-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Une erreur est survenue');
      }

      // Always show success message for security (don't reveal if email exists)
      setIsSubmitted(true);
    } catch (err) {
      // For security, we still show success message even if email doesn't exist
      // This prevents email enumeration attacks
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Success state after submission
  if (isSubmitted) {
    return (
      <div className="max-w-md mx-auto py-8">
        <div className="text-center space-y-4 bg-white dark:bg-slate-800 p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
          {/* Success icon */}
          <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>

          <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
            {t('passwordReset.request.success.title')}
          </h1>

          <p className="text-slate-600 dark:text-slate-400">
            {t('passwordReset.request.success.message').replace('{email}', '')}
            <strong className="text-slate-900 dark:text-white">{email}</strong>
          </p>

          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
            <p className="text-sm text-amber-800 dark:text-amber-400">
              {t('passwordReset.request.success.important')}
            </p>
          </div>

          <div className="pt-4 space-y-3">
            <Link to="/login" className="block w-full btn-primary">
              {t('passwordReset.request.success.backToLogin')}
            </Link>
            <button
              type="button"
              onClick={() => {
                setIsSubmitted(false);
                setEmail('');
              }}
              className="w-full text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              {t('passwordReset.request.success.tryAnotherEmail')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8">
      <div className="text-center space-y-2 mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white">
          {t('passwordReset.request.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('passwordReset.request.subtitle')}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white dark:bg-slate-800 p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700"
      >
        {/* Error message */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Email field */}
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('passwordReset.request.email.label')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder={t('passwordReset.request.email.placeholder')}
            className="input"
            required
            disabled={isLoading}
          />
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('passwordReset.request.submitting')}
            </span>
          ) : (
            t('passwordReset.request.submit')
          )}
        </button>

        {/* Info box */}
        <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t('passwordReset.request.info')}
          </p>
        </div>
      </form>

      {/* Back to login link */}
      <p className="text-center mt-6 text-slate-600 dark:text-slate-400">
        {t('passwordReset.request.rememberPassword')}{' '}
        <Link to="/login" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
          {t('passwordReset.request.login')}
        </Link>
      </p>
    </div>
  );
}
